'use client';
import { ParsedUrlQuery } from 'querystring';

import React, {
  useState,
  useRef,
  MutableRefObject,
  useEffect,
  useCallback,
  useMemo,
} from 'react';

import { useParams, useRouter, useSearchParams } from 'next/navigation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Element } from 'react-scroll';
import dynamic from 'next/dynamic';
import { debounce } from 'lodash-es';
import { setCookie } from 'cookies-next';

import {
  TourPlanOtherContentCategoryTypes,
  TourPrebookAddRequestViewModel,
  TourType,
} from '@api/v3';
import { getOffset } from '@utils/getElementOffset';
import GalleryHeroImages from '@components/galleries/GalleryHeroImages/GalleryHeroImages';
import { Layout, Separator } from '@components/formStyles';
import SummaryCard from '@components/views/tour/cards/summary/SummaryCard';
import Tab from '@components/ui/scroll-tab/ScrollTab';
import IsInViewPort from '@components/ui/IsInViewPort';
import SeoText from '@components/views/landing/components/seoText/seoText';
import useUnlockBasket from '@components/views/hooks/useUnlockBasket';
import { getBasketFromCache } from '@hooks/cache';
import { useToggle } from '@hooks/common';
import BankInstallments from '@components/ui/bank-installments/BankInstallments';
import ScrollToTop from '@components/ScrollToTop/ScrollToTop';
import { BannerDetailWrapper } from '@components/views/hotel/listing/components/Banner/BannerStyle';
import { callService } from '@api/remote-call';
import clean from '@utils/cleanObject';
import { formatISOToDate, URL_DATE_FORMAT } from '@utils/dateUtil';
import {
  TourPackageProps,
  useTourDetailVariationContext,
} from '@contexts/tour-detail/TourDetail';
import { useDevice } from '@contexts/device';
import SearchReservedId from '@components/ui/SearchReservedId';
import { ApiError } from '@api/index';
import { getDetailChatGPTMessage } from '@components/ChatGPTLoader/utils';
import {
  useHideChatGPT,
  useSetChatGPTMessage,
} from '@components/ChatGPTLoader/hooks';
import { TourSchemaOrg } from '@utils/schemaorg/TourSchemaOrg';
import { useBasketMutation } from '@hooks/useBasketInformation';
import { useChatGPTContext } from '@contexts/chatGPT';
import { paramsToObject } from '@components/views/utils';
import { logUnavailableProduct } from '@utils/gtm/unavailable-products-gtm';
import {
  tourDetailAndAddtoCart,
  shipDetailAndAddtoCart,
} from '@utils/newDataLayer/productDetailAndAddtoCart';
import { tourYouLike } from '@utils/newDataLayer/productYouLike';
import { useSkipEffectOnMount } from '@hooks/lifecycle';
import { useRemovePackage } from '@components/views/hooks/useRemovePackage';
import CurrentCartPackagesModal from '@components/views/components/CurrentCartPackageModal';
import CurrentCartTourModal from '@components/views/components/CurrentCartTourModal';
import CurrentCardTourDifferentDatesModal from '@components/views/components/CurrentCardTourDifferentDatesModal';
import { SessionScopeGTM } from '@utils/gtm/session-scope';

import {
  shouldDeleteAllTourPackageRooms,
  TourTypesSalesItemMap,
} from '../components/tour-selection/TourSelectionCard/helpers';
import {
  ImagesWrapper,
  Wrapper,
  StyledButton,
  ButtonsWrapper,
  TabWrapper,
  SeoTextContainer,
  TourDetailContainer,
  TabsSubContainer,
  NoContentWrapper,
} from './styled';
import { formatTourImages } from '../formats';
import TourDetailSkeleton from './components/skeleton';
import { formatDatesRange } from './utils/formats';

const TourDetailContent = dynamic(
  () => import('../components/tour-detail-content/TourDetailContent')
);

const TourOptions = dynamic(
  () => import('../components/tour-options/TourOptions')
);
const TourItinerary = dynamic(
  () => import('../cards/TourItinerary/TourItinerary')
);
const TourRecommendations = dynamic(() => import('./tour-recommendations'));

const ErrorModal = dynamic(() => import('@components/ui/modals/ErrorModal'));

const GeneralLoader = dynamic(() => import('@components/ui/GeneralLoader'));
const ErrorPage = dynamic(() => import('@components/views/error/ErrorPage'));

const TourDetailHeaderMobile = dynamic(
  () => import('./TourDetailHeaderMobile')
);
const TourDetailHeaderDesktop = dynamic(
  () => import('./TourDetailHeaderDesktop')
);
const PriceDetails = dynamic(
  () => import('./components/PriceDetails/PriceDetails')
);
const ErrorInfoModal = dynamic(
  () => import('@components/views/components/ErrorInfoModal/ErrorInfoModal')
);
const LetUsCallYou = dynamic(
  () => import('@components/ui/LetUsCallYou/LetUsCallYou')
);
const TourGallery = dynamic(
  () => import('../components/tour-gallery/TourGallery')
);

const InfoNote = dynamic(() => import('@components/ui/InfoNote'));

const BannerDetail = dynamic(
  () => import('@components/views/hotel/listing/components/Banner/BannerDetail')
);

const AboutTour = dynamic(() => import('./components/AboutTour'));

const ContentList = dynamic(
  () => import('@components/ui/content-list/ContentList')
);

type TourOptionTabs = 'options' | 'properties';

const convertContentTypeIdToString = (
  contentTypeId?: TourPlanOtherContentCategoryTypes
) => {
  switch (contentTypeId) {
    case TourPlanOtherContentCategoryTypes.SERVICES:
      return 'services';
    case TourPlanOtherContentCategoryTypes.VISA:
      return 'visa';
    case TourPlanOtherContentCategoryTypes.GENERAL_CONDITIONS:
      return 'general_conditions';
    case TourPlanOtherContentCategoryTypes.COVID_PRECAUTIONS:
      return 'covid_precautions';
    default:
      return '';
  }
};

type TabsProps = React.PropsWithChildren<{
  xl: boolean;
  searchBarRef: any;
  parentRef: any;
}>;

const Tabs = ({ children, xl, searchBarRef, parentRef }: TabsProps) => {
  const [tabsType, setTabsType] = useState('');
  const bannerRef = useRef<HTMLDivElement | null>(null);

  const scrollHandler = useCallback(() => {
    if (window.scrollY < 225) setTabsType('');
    if (window.scrollY > 225 && !xl) setTabsType('mobile');
    if (window.scrollY > 475 && xl) setTabsType('desktop');
  }, [xl]);

  useEffect(() => {
    const debouncedScrollHandler = debounce(scrollHandler, 200);

    window.addEventListener('scroll', debouncedScrollHandler, {
      passive: true,
    });

    return () => {
      window.removeEventListener('scroll', debouncedScrollHandler);
    };
  }, [scrollHandler]);

  return (
    <TabWrapper
      visible={tabsType === 'desktop' || tabsType === 'mobile'}
      top={
        searchBarRef?.current?.offsetHeight +
        (bannerRef?.current?.offsetHeight ?? 0)
      }
      ref={parentRef}
    >
      <SearchReservedId show={xl} />
      <TabsSubContainer>{children}</TabsSubContainer>
    </TabWrapper>
  );
};
interface TourDetailProps {
  resource: 'Tur' | 'Gemi';
  metaInfo?: Record<string, string>;
  initialData: any;
  tourRecommendationsData?: any;
}

const TourDetail = ({
  resource,
  initialData,
  tourRecommendationsData,
}: TourDetailProps) => {
  const { isDesktop } = useDevice();
  const router = useRouter();
  const params = useParams();
  const searchParams = useSearchParams();
  const query: ParsedUrlQuery = useMemo(() => {
    return paramsToObject(searchParams?.entries());
  }, [searchParams]);
  const { mutateAsync: removePackage, isLoading: isRemovePackageLoading } =
    useRemovePackage();

  const [visibleTab, setVisibleTab] = useState<TourOptionTabs>('options');
  const programsRef = useRef<HTMLElement>();
  const tourDatesRef = useRef<HTMLElement>();
  const searchBarRef = useRef<HTMLDivElement | null>(null);
  const aboutRef = useRef<HTMLElement>();
  const [currentCartProductsModal, setCurrentCartProductsModal] =
    useState(false);
  const [currentTourCartProductsModal, setCurrentTourCartProductsModal] =
    useState(false);
  const [
    currentTourCartDifferentDatesProductsModal,
    setCurrentTourCartDifferentDatesProductsModal,
  ] = useState(false);

  const [showGalleryModal, toggleGalleryModal] = useToggle(false);
  const [showErrorModalDeletePackage, setShowErrorModalDeletePackage] =
    useState(false);

  const showKocAilem = query?.utm_source === 'kocailem' || false;
  const { mutate: basketMutate, data: basketMutationData } =
    useBasketMutation();
  const {
    selectedVariationItems,
    setTourPackages,
    tourPackages,
    removePackageRoom,
    allAddedRoomsSelected,
    removePackageRooms,
  } = useTourDetailVariationContext();

  const { setItemId, setItemType } = useChatGPTContext();

  const [showInstallments, setShowInstallments] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<HTMLElement>();
  const tabsWrapperRef = useRef<HTMLDivElement | null>(null);
  const [showLetUsCallYou, setShowLetUsCallYou] = useState<boolean>(false);
  const [tourInfo, setTourInfo] = useState<string>();
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [isClientSide, setIsClientSide] = useState(false);
  const isLogged = useRef(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [errorModalStatusMessage, setErrorModalStatusMessage] = useState({
    title: '',
    description: '',
  });

  const handleConfirmRemoveCurrentCartPackage = async (): Promise<void> => {
    if (!basketMutationData) return;

    setCurrentCartProductsModal(false);
    await unlockBasketAsync();

    await removePackage({
      basketId: basketMutationData?.id,
      itemId: basketMutationData?.hotels?.[0]?.itemId ?? '',
    });

    prebookMutation.mutate();
  };

  const handleConfirmRemoveCurrentCartTour = async (): Promise<void> => {
    if (!basketMutationData) return;

    setCurrentCartProductsModal(false);
    await unlockBasketAsync();
    const items: any[] = [];
    if (basketMutationData.tours && basketMutationData.tours.length > 0) {
      items.push(...basketMutationData.tours);
    }
    if (items?.length) {
      for (let i = 0; i < items?.length; i++) {
        await callService(
          { service: 'BasketsService', method: 'remove', version: 'v3' },
          {
            basketId: basketMutationData?.id,
            itemId: items[i].itemId,
          }
        );
      }
    }
    prebookMutation.mutate();
  };
  const queryClient = useQueryClient();
  useEffect(() => {
    if (initialData) {
      queryClient.setQueryData(
        ['tourDetail', JSON.stringify(query)],
        initialData
      );
    }
  }, [initialData, query, queryClient]);

  const {
    data,
    isLoading,
    isError: isGetTourDetailError,
    isFetching: isTourFetching,
  } = useQuery(
    ['tourDetail', JSON.stringify(query)],
    () => {
      const basketId = getBasketFromCache()?.id;
      const formattedDate = query
        ? Array.isArray(query.date)
          ? query.date // Keep arrays as is
          : typeof query.date === 'string'
          ? query.date.split(',') // Split comma-separated strings into an array
          : []
        : [];
      return callService(
        { service: 'ToursService', method: 'getTour', version: 'v3' },
        clean({
          slug: String(params?.slug),
          date: formattedDate,
          ...(basketId ? { basketId } : {}),
        })
      );
    },
    {
      enabled: Boolean(params?.slug && isClientSide),
      initialData: initialData,
      onSuccess: () => {
        setIsRedirecting(false);
      },
    }
  );
  SessionScopeGTM(
    'Detay Sayfası',
    data.type === TourType.CRUISE
      ? 'DP - Yurt Dışı Gemi'
      : data.type === TourType.ABROAD
      ? 'DP - Yurt Dışı Tur'
      : 'DP - Yurt İçi Tur'
  );
  useSkipEffectOnMount(() => {
    if (query && !isClientSide) {
      setIsClientSide(true);
    }
  }, [query]);

  useHideChatGPT(false);
  useSetChatGPTMessage(getDetailChatGPTMessage(data?.name ?? ''));

  const isExpedia =
    data?.type === TourType.ABROAD || data?.type === TourType.CRUISE;

  const {
    unlockBasketAsync,
    isError: basketError,
    cleanBasketSignal,
  } = useUnlockBasket({
    basketId: getBasketFromCache()?.id ?? '',
  });

  const addBasket = async () => {
    const basketId = localStorage.getItem('basketId');

    const body = selectedVariationItems?.map(
      (variation): TourPrebookAddRequestViewModel => ({
        id: data?.id || 0,
        packageDetailId: variation.packageDetailId || 0,
        companyCommissionAmount: variation.companyCommissionAmount || 0,
        // Because of date format, childBirthdates doesn't show as nullable.
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        guests: variation.guests?.map((guest) => ({
          adultCount: guest.adultNumber,
          ...(guest.childList
            ? {
                childBirthdates: guest.childList?.map((child) =>
                  formatISOToDate(String(child.date), URL_DATE_FORMAT)
                ),
              }
            : {}),
        })),
        extraServices: [],
        promotions: variation?.promotionInfoList?.[0]?.promotionId
          ? [variation?.promotionInfoList?.[0]?.promotionId]
          : [],
      })
    );

    resource === 'Tur'
      ? tourDetailAndAddtoCart(data, query, selectedVariationItems)
      : shipDetailAndAddtoCart(data, query, selectedVariationItems);

    await unlockBasketAsync();

    const basketState = await cleanBasketSignal;

    return callService(
      { service: 'PrebookService', method: 'prebookAddTour', version: 'v3' },
      {
        basketId: basketState === 'stale' || !basketId ? '' : basketId,
        body,
      }
    );
  };

  const { data: tourRecommendations } = useQuery(
    ['tourRecommendations', data?.id],
    () =>
      callService(
        {
          service: 'ToursService',
          method: 'tourRecommendations',
          version: 'v3',
        },
        {
          body: {
            selectedTourId: data?.id || 0,
            listedTourIds: [],
            page: 0,
            size: 20,
          },
        }
      ),
    {
      enabled: Boolean(data?.id && data?.type != 'Cruise' && isClientSide),
      initialData: tourRecommendationsData,
    }
  );

  const prebookMutation = useMutation(
    ['tourPrebook'],
    async () => addBasket(),
    {
      onSuccess: ({ basketId }) => {
        localStorage.setItem('basketId', basketId);
        setCookie('basketId', basketId);
        router.push(`/seyahat-planlama?bid=${basketId}`);
      },
      onError: (error: ApiError) => {
        if (error?.body?.errors?.[0]?.code === 'LOYALTY_SWITCH_ERROR') {
          const { basketId, errors } = error.body;
          sessionStorage.setItem(
            'switchError',
            JSON.stringify({
              message: errors?.[0]?.message || '',
              items: errors?.map((item) => item.errorItemId),
            })
          );
          localStorage.setItem('basketId', basketId);
          setCookie('basketId', basketId);
          setErrorModalStatusMessage({
            title: 'Uyarı',
            description: 'Loyalty promosyonu kazanmadan ürün eklenmektedir. ',
          });
          setTimeout(() => {
            router.push(`/seyahat-planlama?bid=${basketId}`);
          }, 1500);
        } else {
          setErrorModalStatusMessage({
            title: 'Ooops',
            description:
              error?.body?.errors?.[0]?.detail ||
              'Sepete ürün eklemeye çalışırken, genel bir hata oluştu, tekrar deneyiniz.',
          });
        }
      },
    }
  );

  useEffect(() => {
    if (prebookMutation.isError || basketError) {
      setShowErrorModal(true);
    }
  }, [prebookMutation.isError, basketError]);

  const handleNavigatePrograms = (): void =>
    programsRef?.current?.scrollIntoView({ behavior: 'smooth' });
  const handleNavigateDates = (): void => {
    visibleTab !== 'options' && setVisibleTab('options');
    tourDatesRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (data?.dayCount && data?.nightCount) {
      const info =
        data?.nightCount + ' gece, ' + data?.dayCount + ' gün, kişi başı';
      setTourInfo(info);
    }
    if (!isLogged.current && data && data?.packages?.length === 0) {
      const formattedDate = query.date ? formatDatesRange(query.date) : '';
      if (formattedDate === '') return;
      logUnavailableProduct(
        data.name || '',
        formattedDate,
        data.type === TourType.ABROAD
          ? 'Yurtdışı Turlar'
          : data.type === TourType.CRUISE
          ? 'Gemi'
          : 'Yurtiçi Turlar'
      );
      isLogged.current = true;
    }
  }, [data]);
  const prevItemId = useRef<number | null>(null);
  const prevItemType = useRef<string>('');
  useEffect(() => {
    if (!data?.id) return;

    if (prevItemId.current !== data.id) {
      setItemId(data.id);
      prevItemId.current = data.id;
    }

    if (prevItemType.current !== 'Tour') {
      setItemType('Tour');
      prevItemType.current = 'Tour';
    }
  }, [data]);

  useEffect(() => {
    return () => {
      setItemId(null);
      setItemType('');
    };
  }, []);

  // ==========================================
  // Calculate the selected packages total price
  // ==========================================
  const toggleInstallments = (): void => setShowInstallments((state) => !state);
  const packagesTotalPrice = useMemo(() => {
    const totalTL = selectedVariationItems?.reduce(
      (acc, p) => (p.tryTotalDiscountedSellingPrice ?? 0) + acc,
      0
    );

    const totalEUR = selectedVariationItems?.reduce(
      (acc, p) => (p.originalTotalDiscountedSellingPrice ?? 0) + acc,
      0
    );

    return {
      totalTL,
      totalEUR,
      originalCurrency: Object.values(selectedVariationItems)?.[0]
        ?.originalCurrency,
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVariationItems]);

  const TOUR_CONTENTS = [
    {
      title: `${resource} Süresi`,
      response: `${data?.nightCount} gece / ${data?.dayCount} gün`,
    },
  ];

  const checkValidContents = (contentTypeId: string) => {
    return data?.contents?.some((item) => item.contentTypeId === contentTypeId);
  };

  const handleNavigateAboutUs = (): void =>
    aboutRef?.current?.scrollIntoView({ behavior: 'smooth' });

  const stickyHeaderTabs = (resource: string) => [
    { key: 'overview', title: 'Genel Bakış', show: true },
    { key: 'features', title: 'Tarihler/Özellikler', show: true },
    {
      key: 'programs',
      title: `${resource} Programı`,
      show: data?.programs?.length ? true : false,
    },
    ...(checkValidContents(TourPlanOtherContentCategoryTypes.SERVICES)
      ? [{ key: 'services', title: 'Hizmetler', show: true }]
      : []),
    ...(checkValidContents(TourPlanOtherContentCategoryTypes.VISA)
      ? [{ key: 'visa', title: 'Vize', show: true }]
      : []),
    ...(checkValidContents(TourPlanOtherContentCategoryTypes.GENERAL_CONDITIONS)
      ? [{ key: 'general_conditions', title: 'Genel Şartlar', show: true }]
      : []),
    ...(checkValidContents(TourPlanOtherContentCategoryTypes.COVID_PRECAUTIONS)
      ? [{ key: 'covid_precautions', title: 'Covid Önlemleri', show: true }]
      : []),
  ];

  // ====================================
  // scroll the active tab when going out of the viewport
  // ===================================

  const handleActiveScrollTab = () => {
    const targetTab = tabsWrapperRef?.current?.children
      .item(0)
      ?.querySelector('.active') as HTMLElement;

    if (targetTab) {
      setActiveTab(targetTab);
    }
  };

  const errorInfoModalClosed = () => {
    setShowErrorModal(false);
    setErrorModalStatusMessage({
      title: '',
      description: '',
    });
  };

  useEffect(() => {
    if (activeTab) {
      const isInView =
        getOffset(activeTab).left + 0 >= 0 &&
        getOffset(activeTab).left - 0 <= window.innerWidth;

      if (isInView) return;
      tabsWrapperRef.current?.children.item(0)?.scrollTo({
        left: getOffset(activeTab).left,
        top: getOffset(activeTab).top,
        behavior: 'smooth',
      });
    }
  }, [activeTab]);

  useEffect(() => {
    window.addEventListener('scroll', debounce(handleActiveScrollTab, 200), {
      passive: true,
    });

    return () => {
      window.removeEventListener('scroll', handleActiveScrollTab);
    };
  }, []);

  const isTourSafe = useMemo(() => {
    return data?.features?.find((item) => item.key === 21) ? true : false;
  }, [data]);

  const toggleLetUsCallYou = useCallback(
    () => setShowLetUsCallYou((state) => !state),
    []
  );
  const scrollToBottom = () => {
    aboutRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleDrawerRoomRemoved = (
    packageDetailId: number,
    roomId: string,
    roomIndex: number
  ) => {
    const currentPackage = tourPackages?.find(
      (packageItem) => packageItem.packageDetailId === packageDetailId
    );
    const shouldDeleteAllRooms = shouldDeleteAllTourPackageRooms(
      roomIndex,
      currentPackage
    );

    if (shouldDeleteAllRooms) return removePackageRooms(packageDetailId ?? -1);

    removePackageRoom(packageDetailId ?? -1, roomId);
  };

  useEffect(() => {
    if (!data?.packages) return;

    const tourPackages: TourPackageProps[] = [];

    data?.packages?.forEach((item) =>
      tourPackages.push({
        packageDetailId: item.tourPackageDetailId,
        title: `${formatISOToDate(item.departureDate)} - ${formatISOToDate(
          item.arrivalDate
        )}`,
        dayCount: data?.dayCount || 0,
        nightCount: data?.nightCount || 0,
      })
    );

    setTourPackages(tourPackages);
  }, [data]);

  const [startImage, setStartImage] = useState(-1);

  const viewGallery = useCallback(
    (index?: number) => {
      if (index !== undefined) setStartImage(index);
      setTimeout(() => toggleGalleryModal());
    },
    [toggleGalleryModal]
  );

  useEffect(() => {
    if (showErrorModalDeletePackage || showErrorModal) {
      setIsRedirecting(false);
    }
  }, [showErrorModalDeletePackage, showErrorModal]);

  useEffect(() => {
    if (resource === 'Tur') tourDetailAndAddtoCart(data, query, []);
    if (resource === 'Gemi') shipDetailAndAddtoCart(data, query, []);
  }, [data]);

  const showDetailPricesDrawer = useMemo(() => {
    let visible = false;

    if (!tourPackages || tourPackages.length === 0) return false;
    tourPackages?.forEach((packageItem) => {
      if (packageItem.rooms?.length) {
        visible = true;
      }
    });

    return visible;
  }, [tourPackages]);

  useEffect(() => {
    if (prebookMutation.isError || prebookMutation.isLoading) {
      document.body.style.overflow = 'hidden';

      document.body.style.overflowY = 'hidden';
    } else {
      document.body.style.overflow = 'initial';

      document.body.style.overflowY = 'initial';
    }

    return () => {
      document.body.style.overflow = 'initial';
      document.body.style.overflowY = 'initial';
    };
  }, [
    prebookMutation.isError,
    prebookMutation.isLoading,
    prebookMutation.isSuccess,
  ]);

  const allPackageDetailsAreSame = (selectedItems) => {
    if (!selectedItems || selectedItems.length <= 1) {
      return true;
    }
    const firstPackageDetailId = selectedItems[0]?.packageDetailId;
    if (firstPackageDetailId === undefined || firstPackageDetailId === null) {
      return false;
    }
    return selectedItems.every(
      (item) => item.packageDetailId === firstPackageDetailId
    );
  };

  const handleNavigateTravelPlanning = async (): Promise<void> => {
    const basketId =
      typeof window !== 'undefined' && localStorage.getItem('basketId');
    if (!allPackageDetailsAreSame(selectedVariationItems)) {
      setCurrentTourCartDifferentDatesProductsModal(true);
      return;
    }
    if (basketId) {
      basketMutate(basketId, {
        onSuccess: (data) => {
          if (data?.isPackageBasket) {
            setCurrentCartProductsModal(true);
          } else if (data.tours.length > 0) {
            setCurrentTourCartProductsModal(true);
          } else {
            setIsRedirecting(true);
            prebookMutation.mutate();
          }
        },
      });
    } else {
      setIsRedirecting(true);
      prebookMutation.mutate();
    }
  };
  const isVideo = data?.videos && data?.videos?.length > 0 ? true : false;
  if (isGetTourDetailError) return <ErrorPage />;

  return (
    <>
      {/* <BreadCrumbListSchemaOrg data={breadcrumbItems} /> */}
      {data && <TourSchemaOrg tourDetail={data} />}
      <TourDetailContainer>
        <ScrollToTop />
        {data?.bankInstallments && showInstallments ? (
          <BankInstallments
            bankInstallments={data.bankInstallments}
            onClose={toggleInstallments}
          />
        ) : null}

        {currentCartProductsModal ? (
          <CurrentCartPackagesModal
            onClose={() => setCurrentCartProductsModal(false)}
            onConfirm={() => {
              handleConfirmRemoveCurrentCartPackage();
            }}
          />
        ) : null}

        {currentTourCartProductsModal ? (
          <CurrentCartTourModal
            onClose={() => setCurrentTourCartProductsModal(false)}
            onConfirm={() => {
              handleConfirmRemoveCurrentCartTour();
              setCurrentTourCartProductsModal(false);
            }}
          />
        ) : null}

        {currentTourCartDifferentDatesProductsModal ? (
          <CurrentCardTourDifferentDatesModal
            onClose={() => setCurrentTourCartDifferentDatesProductsModal(false)}
          />
        ) : null}

        {isDesktop ? (
          <TourDetailHeaderDesktop
            setIsRedirecting={setIsRedirecting}
            resource={resource}
            data={data}
          />
        ) : (
          <TourDetailHeaderMobile
            setIsRedirecting={setIsRedirecting}
            searchBarRef={searchBarRef}
            resource={resource}
            detail={true}
            data={data}
          />
        )}
        <Tabs
          xl={isDesktop}
          searchBarRef={searchBarRef}
          parentRef={tabsWrapperRef}
        >
          <Tab tabs={stickyHeaderTabs(resource)} />
        </Tabs>
        {isLoading || !data ? (
          <TourDetailSkeleton />
        ) : (
          <>
            <ImagesWrapper>
              <GalleryHeroImages
                isBadge={data?.features?.some(
                  (item) => item.value === 'Misafirler Öneriyor'
                )}
                isSeturExecutive={data.seturExecutive}
                badges={data?.badges}
                isSafe={isTourSafe}
                images={formatTourImages(data?.images?.slice(0, 3), data.name)}
                totalImages={data?.images?.length}
                viewGallery={viewGallery}
                isVideo={isVideo}
                tour={true}
                detail={true}
              />
            </ImagesWrapper>
            <Layout>
              <Wrapper>
                <Element name='overview' className='program'>
                  <SummaryCard
                    isBadge={data?.features?.some(
                      (item) => item.value === 'Misafirler Öneriyor'
                    )}
                    installments={data.installments}
                    name={data.name || ''}
                    description={data.earliestDepartureDate || ''}
                    whereToGo={data.placesToGo}
                    prices={data.lowestPrice}
                    onNavigate={handleNavigateDates}
                    tourType={data?.type}
                    onReadMore={handleNavigateAboutUs}
                    date={data.earliestDepartureDate}
                    isEmpty={data?.packages?.length === 0}
                    toggleLetUsCallYou={toggleLetUsCallYou}
                    roomInformation={tourInfo}
                    isExpedia={isExpedia}
                    showKocAilem={showKocAilem}
                    toggleInstallments={toggleInstallments}
                    promotions={data.promotions}
                    itemId={data.id}
                    imageUrl={data?.images?.[0]?.url ?? ''}
                    segments={data.tags}
                  />

                  {data?.banner?.images || data?.banner?.responsiveImages ? (
                    <>
                      <Separator />
                      <Element
                        onClick={() => scrollToBottom()}
                        name='about'
                        className='element'
                      >
                        {isDesktop && data?.banner ? (
                          <>
                            {data?.banner?.images?.[0]?.url ? (
                              <BannerDetailWrapper>
                                <BannerDetail
                                  url={data.banner?.images[0]?.url}
                                  alt={data.banner?.images[0]?.alt}
                                />
                              </BannerDetailWrapper>
                            ) : null}
                          </>
                        ) : (
                          <>
                            {data?.banner?.responsiveImages?.[0]?.url ? (
                              <BannerDetailWrapper>
                                <BannerDetail
                                  url={data?.banner?.responsiveImages?.[0]?.url}
                                  alt={data?.banner?.responsiveImages?.[0]?.alt}
                                />
                              </BannerDetailWrapper>
                            ) : null}
                          </>
                        )}
                      </Element>
                    </>
                  ) : null}
                  <Separator />
                </Element>
                <Element name='features' className='element'>
                  <ButtonsWrapper>
                    <StyledButton
                      size='large'
                      variant='Primary'
                      onClick={() => setVisibleTab('options')}
                      outline={visibleTab === 'properties'}
                      hasHover={false}
                      cornerRadius='8px 0 0 8px'
                    >
                      {resource} Tarihleri
                    </StyledButton>
                    <StyledButton
                      variant='Primary'
                      onClick={() => setVisibleTab('properties')}
                      outline={visibleTab === 'options'}
                      hasHover={false}
                      cornerRadius='0 8px 8px 0'
                    >
                      {resource} Özellikleri
                    </StyledButton>
                  </ButtonsWrapper>
                  {visibleTab === 'options' ? (
                    <div ref={tourDatesRef as MutableRefObject<HTMLDivElement>}>
                      {data?.packages?.length > 0 ? (
                        <TourOptions
                          days={{
                            dayCount: data.dayCount,
                            nightCount: data.nightCount,
                          }}
                          packages={data.packages}
                          entityName={data.name}
                          tourType={data?.type}
                          tourId={data.id}
                          data={data}
                        />
                      ) : (
                        <InfoNote>Uygun tur paket bulunamamaktadır.</InfoNote>
                      )}
                    </div>
                  ) : (
                    <ContentList
                      title={`${resource} Özellikleri`}
                      contents={TOUR_CONTENTS}
                      navigationText={`${resource} Programına Git`}
                      onNavigation={handleNavigatePrograms}
                    />
                  )}
                </Element>
                <Separator />

                {data.programs?.length ? (
                  <div ref={programsRef as MutableRefObject<HTMLDivElement>}>
                    <Element name='programs' className='element'>
                      <TourItinerary data={data.programs} />
                    </Element>
                  </div>
                ) : null}
                {data.metaContent ??
                data?.metaContent ??
                tourRecommendations ? (
                  <NoContentWrapper />
                ) : null}

                {data.metaContent ? (
                  <div ref={aboutRef as MutableRefObject<HTMLDivElement>}>
                    <Element name='about' className='element'>
                      <AboutTour description={data.metaContent} />
                      <Separator />
                    </Element>
                  </div>
                ) : null}
                {data.contents
                  ? data.contents.map((item, key) => (
                      <Element
                        name={
                          convertContentTypeIdToString(item.contentTypeId) || ''
                        }
                        className='element'
                        key={key}
                      >
                        <TourDetailContent
                          title={item.name || 'Ekstra Bilgiler'}
                          description={item.description}
                        />
                        <Separator />
                      </Element>
                    ))
                  : null}
                {tourRecommendations ? (
                  <IsInViewPort
                    onViewVisible={() => {
                      tourYouLike(
                        tourRecommendations?.data?.slice(0, 10),
                        false
                      );
                    }}
                  >
                    <TourRecommendations
                      setIsRedirecting={setIsRedirecting}
                      isExpedia={isExpedia}
                      tourRecommendations={tourRecommendations}
                      date={query?.date}
                    />
                  </IsInViewPort>
                ) : null}
              </Wrapper>
            </Layout>
            <TourGallery
              visible={showGalleryModal}
              toggleModal={toggleGalleryModal}
              features={data.features}
              images={data.images}
              dayCount={data.dayCount}
              nightCount={data.nightCount}
              lowestPrice={data.lowestPrice}
              tourName={data.name || ''}
              startImage={startImage}
              videos={data.videos ?? []}
            />
            {data.metaContent ? (
              <SeoTextContainer>
                <SeoText text={data.metaContent || ''} />
              </SeoTextContainer>
            ) : null}

            {showDetailPricesDrawer ? (
              <PriceDetails
                packages={tourPackages || []}
                onNavigate={handleNavigateTravelPlanning}
                onRemove={handleDrawerRoomRemoved}
                isExpedia={isExpedia || data.type === TourType.CRUISE}
                totalPrice={packagesTotalPrice.totalTL}
                originalPrice={packagesTotalPrice.totalEUR}
                originalCurrency={packagesTotalPrice.originalCurrency}
                isLoading={prebookMutation.isLoading}
                disabled={!allAddedRoomsSelected}
              />
            ) : null}
          </>
        )}

        <LetUsCallYou
          visibleLetUsCallYouModal={showLetUsCallYou}
          token=''
          onClose={toggleLetUsCallYou}
          entityId={String(data?.id || '')}
          entityName={data?.name || ''}
          currency={data?.lowestPrice?.currency}
          price={data?.lowestPrice?.price || 0}
          salesItemType={TourTypesSalesItemMap[data?.type || TourType.ABROAD]}
          formCheckInDate={null}
          formCheckOutDate={null}
          hotelRoomList={[]}
        />
        {prebookMutation.isLoading ||
        isRedirecting ||
        isTourFetching ||
        isRemovePackageLoading ? (
          <GeneralLoader />
        ) : null}

        {showErrorModal ? (
          <ErrorInfoModal
            title={errorModalStatusMessage.title}
            description={errorModalStatusMessage.description}
            onCancel={errorInfoModalClosed}
          />
        ) : null}
        {showErrorModalDeletePackage ? (
          <ErrorModal
            onCancel={() => setShowErrorModalDeletePackage(false)}
            message='Ürün sepetten silinirken bir hata oluştu.'
          />
        ) : null}
      </TourDetailContainer>
    </>
  );
};

export default TourDetail;
